import { makeStyles } from '@mui/styles';
import { memo, useContext } from 'react';
import { Typography, Box } from '@mui/material';
import { Table, WithFetch, FetchData, WithTableLayout } from '@qrios/frontend-library';

import endpoints from '../../../../../../api/endpoints';
import { UrlParam } from '../../../../../../api/interfaces';
import { ClientBalance, ClientBalanceContext } from '../../../client-balance';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import { columns } from './columns';
import BillingInformationRow from './billingInformationRow';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { LatestCreditsHistoryResponse } from './interfaces';
import { HELPER_TEXT } from './consts';

const useStyles = makeStyles(theme => ({
  subtitleContainer: {
    minHeight: theme.customComponents.card.header.height,
    display: 'flex',
    flexDirection: 'column'
  },
  historyTitle: {
    [theme.breakpoints.up('md')]: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  }
}));

const BillingInformation = () => {
  const classes = useStyles();
  const { getClientBalance } = useContext(ClientBalanceContext);

  const initFetchOptions = { urlParams: [['count', '20']] as UrlParam[] };

  const { balance, pendingDebits, pendingCredits } = getClientBalance('all') as ClientBalance;

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow={{ xs: 'auto', md: 'hidden' }}>
      <Box data-testid="billing-information" mx={3} height={{ xs: '100%', md: '30%' }}>
        <BillingInformationRow label="Pending Debits" amount={pendingDebits} tooltip={HELPER_TEXT.PENDING_DEBITS} />
        <BillingInformationRow label="Pending Credits" amount={pendingCredits} tooltip={HELPER_TEXT.PENDING_CREDITS} />
        <BillingInformationRow label="Balance" amount={balance} variant="h3" useColor tooltip={HELPER_TEXT.BALANCE} />
      </Box>
      <Box
        display="flex"
        data-testid="latest-transactions-history"
        flexDirection="column"
        height={{ xs: '100%', md: '70%' }}
        overflow={{ xs: 'auto', md: 'hidden' }}
      >
        <Box py={2} px={3} className={classes.subtitleContainer}>
          <Typography className={classes.historyTitle} variant="h2">
            Latest transactions history
          </Typography>
        </Box>
        <WithFetch
          fetchProvider={fetchWithTokenRefresh}
          pathname={endpoints.operationHistoryLatest}
          options={initFetchOptions}
          withError={false}
        >
          {({
            data: latestTranscationsHistoryData,
            refresh: latestTranscationsHistoryRefresh,
            loading: latestTranscationsHistoryLoading,
            error: latestTranscationsHistoryError
          }: FetchData<LatestCreditsHistoryResponse[] | undefined>) => (
            <WithTableLayout>
              <Table
                dataGridProps={{
                  loading: latestTranscationsHistoryLoading,
                  error: {
                    message: latestTranscationsHistoryError,
                    refresh: () => latestTranscationsHistoryRefresh(initFetchOptions)
                  },
                  rows: latestTranscationsHistoryData ? mapApiDataIntoTableFormat(latestTranscationsHistoryData) : [],
                  columns,
                  emptyMsg: "You don't have any latest transactions to display"
                }}
              />
            </WithTableLayout>
          )}
        </WithFetch>
      </Box>
    </Box>
  );
};

export default memo(BillingInformation);
